import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import matter from 'gray-matter';
import { useThemeContext } from '../context/ThemeContext';
import { ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import { Buffer } from 'buffer';
import { ConceptTreeRenderer } from './components/Concept/ConceptTreeRenderer';

import { renderInlineContent } from '../utils/workspaceUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer;
}

const ConceptVisualizer = ({ content, metadata: passedMetadata, onNextChapter, nextChapterTitle, onMastered, categoryId }) => {
  const { mode } = useThemeContext();
  const isLight = mode === 'light';

  // Helper to safely get numeric coordinates
  const safeCoord = (val, fallback = 0) => {
    const num = Number(val);
    return isNaN(num) ? fallback : num;
  };

  const metadata = useMemo(() => {
    if (passedMetadata) return passedMetadata;
    try {
      const parsed = matter(content);
      return parsed.data;
    } catch {
      return null;
    }
  }, [content, passedMetadata]);

  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
  }, [content]);

  // --- VERTICAL RESIZING LOGIC ---
  const [canvasHeight, setCanvasHeight] = useState(280);
  const visualizerRef = useRef(null);
  const isDraggingVertical = useRef(false);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    isDraggingVertical.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDraggingVertical.current || !visualizerRef.current) return;
      const rect = visualizerRef.current.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      const newHeight = Math.min(600, Math.max(200, relativeY - 48));
      setCanvasHeight(newHeight);
    };

    const onMouseUp = () => {
      isDraggingVertical.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Memoize values that depend on metadata
  const frameData = useMemo(() => {
    if (!metadata || !metadata.frames) return null;
    const { title, frames } = metadata;
    return { title, frames };
  }, [metadata]);

  const currentFrame = useMemo(() => {
    if (!frameData) return null;
    return frameData.frames[step] || frameData.frames[0];
  }, [frameData, step]);

  const isComplete = useMemo(() => {
    if (!frameData) return false;
    return step === frameData.frames.length - 1;
  }, [frameData, step]);

  useEffect(() => {
    if (onMastered) onMastered(isComplete);
  }, [isComplete, onMastered]);

  if (!metadata || !metadata.frames) return null;

  const { title, frames } = metadata;

  // Render the universal canvas engine
  const renderCanvas = () => {
    const canvas = currentFrame.canvas;

    if (metadata.dsType === 'tree' && metadata.tree && metadata.root) {
      return (
        <div style={{ width: '100%', padding: '20px' }}>
          <ConceptTreeRenderer
            tree={metadata.tree}
            rootId={metadata.root}
            memory={currentFrame.memory || {}} // Note: ConceptFrame might need memory, or if pointers are in currentFrame.canvas.nodes...
            highlightIndices={[
              ...(currentFrame.highlightIndices || []),
              ...(currentFrame.pointers || []).map((p) => typeof p === 'object' ? p.index : p)
            ]}
            isLight={isLight}
          />
        </div>
      );
    }

    if (!canvas) {
      return <div style={{ textAlign: 'center', color: '#64748b' }}>No canvas data provided for this frame.</div>;
    }

    const resolveTextColor = (baseColor, isContainer = false) => {
      if (isLight) return baseColor || (isContainer ? "#1e293b" : "#0f172a");
      if (!baseColor) return isContainer ? "#fff" : "#f8fafc";

      const darkMap = {
        "#0f172a": "#f8fafc",
        "#1e293b": "#e2e8f0",
        "#1e1e24": "#e2e8f0",
        "#1e3a8a": "#60a5fa",
        "#64748b": "#94a3b8",
        "#334155": "#cbd5e1",
        "#000000": "#ffffff",
        "black": "#ffffff"
      };

      return darkMap[baseColor.toLowerCase()] || baseColor;
    };

    // Helper: compute node dimensions (shared between bounding box calc and rendering)
    const getNodeDims = (n) => {
      const type = n.type || 'pill';
      const isBox = type === 'box';
      const baseCharWidth = isBox ? 9 : 10;
      const minWidth = n.label ? (isBox ? 180 : 80) : 0;
      const lines = n.label ? n.label.split('\n') : [''];
      const longestLineLength = Math.max(...lines.map(l => l.length));
      const contentWidth = n.label ? (longestLineLength * baseCharWidth + (isBox ? 32 : 24)) : 0;
      const width = Math.max(n.width || 0, minWidth, contentWidth);
      const minHeight = n.label ? (isBox ? 80 : 45) : 0;
      const contentHeight = n.label ? (isBox ? (lines.length * 24 + 24) : (lines.length * 20 + 20)) : 0;
      const height = Math.max(n.height || 0, minHeight, contentHeight);
      return { width, height, lines, type, isBox };
    };

    const SCALE_X = 1.3;
    const SCALE_Y = 1.15;

    // Calculate bounding box
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

    (canvas.nodes || []).forEach(n => {
      const { width, height, isBox } = getNodeDims(n);
      const sx = safeCoord(n.x) * SCALE_X;
      const sy = safeCoord(n.y) * SCALE_Y;
      minX = Math.min(minX, sx - width / 2);
      maxX = Math.max(maxX, sx + width / 2);
      const labelHeadroom = (isBox && n.label) ? 25 : 0;
      minY = Math.min(minY, sy - height / 2 - labelHeadroom);
      maxY = Math.max(maxY, sy + height / 2);
    });

    if (minX === Infinity) { minX = 0; maxX = 600; minY = 0; maxY = 400; }

    // Add extra padding for edge labels
    minX -= 60; maxX += 60;
    minY -= 60; maxY += 60;

    const viewWidth = maxX - minX;
    const viewHeight = maxY - minY;

    // Helper: get the edge of a node rectangle closest to a target point
    const getEdgePoint = (node, targetX, targetY) => {
      const { width, height } = getNodeDims(node);
      const cx = safeCoord(node.x) * SCALE_X;
      const cy = safeCoord(node.y) * SCALE_Y;
      const hw = width / 2 + 2;
      const hh = height / 2 + 2;

      const dx = targetX - cx;
      const dy = targetY - cy;
      if (dx === 0 && dy === 0) return { x: cx + hw, y: cy };

      // Calculate intersection with rectangle edges
      const scaleX = Math.abs(dx) / hw;
      const scaleY = Math.abs(dy) / hh;

      if (scaleX > scaleY) {
        // Hits left or right edge
        const sign = dx > 0 ? 1 : -1;
        return { x: cx + sign * hw, y: cy + dy / scaleX };
      } else {
        // Hits top or bottom edge
        const sign = dy > 0 ? 1 : -1;
        return { x: cx + dx / scaleY, y: cy + sign * hh };
      }
    };

    // Unique gradient/glow IDs for this render
    const gradientId = `glow-${step}`;

    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg
          viewBox={`${minX} ${minY} ${viewWidth} ${viewHeight}`}
          style={{ width: '100%', height: '100%', overflow: 'visible' }}
        >
          {/* ─── SVG DEFINITIONS: Arrowheads, Glows, Gradients ─── */}
          <defs>
            {/* Solid arrow marker */}
            <marker id={`arrow-solid-${step}`} viewBox="0 0 12 12" refX="10" refY="6" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
              <path d="M 0 0 L 12 6 L 0 12 Z" fill={isLight ? '#94a3b8' : '#64748b'} />
            </marker>
            {/* Dashed arrow marker (red for danger) */}
            <marker id={`arrow-dashed-${step}`} viewBox="0 0 12 12" refX="10" refY="6" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
              <path d="M 0 0 L 12 6 L 0 12 Z" fill="#ef4444" />
            </marker>
            {/* Per-node colored arrow markers */}
            {(canvas.edges || []).map((edge, i) => {
              const color = edge.color || (edge.dashed ? '#ef4444' : (isLight ? '#94a3b8' : '#64748b'));
              return (
                <marker key={`marker-${edge.id}`} id={`arrow-${edge.id}-${step}`} viewBox="0 0 12 12" refX="10" refY="6" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
                  <path d="M 0 0 L 12 6 L 0 12 Z" fill={color} />
                </marker>
              );
            })}
            {/* Node glow filters */}
            {(canvas.nodes || []).filter(n => n.color && n.type !== 'text').map(node => (
              <filter key={`filter-${node.id}`} id={`glow-${node.id}-${step}`} x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feFlood floodColor={node.color} floodOpacity={isLight ? "0.15" : "0.3"} result="color" />
                <feComposite in="color" in2="blur" operator="in" result="shadow" />
                <feMerge>
                  <feMergeNode in="shadow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            ))}
          </defs>

          {/* ─── 1. DRAW EDGES (with arrowheads + labels) ─── */}
          <AnimatePresence>
            {(canvas.edges || []).map((edge, edgeIndex) => {
              const fromNode = canvas.nodes?.find(n => n.id === edge.from);
              const toNode = canvas.nodes?.find(n => n.id === edge.to);
              if (!fromNode || !toNode) return null;

              const highlightIndices = currentFrame.highlightIndices || [];
              const pointers = (currentFrame.pointers || []).map((p) => typeof p === 'object' ? p.index : p);
              const allHighlightIndices = [...highlightIndices, ...pointers];
              const isHighlighted = allHighlightIndices.map(String).includes(String(fromNode.id)) || allHighlightIndices.map(String).includes(String(toNode.id));

              const fcx = safeCoord(fromNode.x) * SCALE_X;
              const fcy = safeCoord(fromNode.y) * SCALE_Y;
              const tcx = safeCoord(toNode.x) * SCALE_X;
              const tcy = safeCoord(toNode.y) * SCALE_Y;

              // Calculate edge points at node boundaries
              const fromPt = getEdgePoint(fromNode, tcx, tcy);
              const toPt = getEdgePoint(toNode, fcx, fcy);

              const edgeColor = edge.color || (edge.dashed ? (isLight ? '#f87171' : '#ef4444') : (isHighlighted ? '#3b82f6' : (isLight ? '#94a3b8' : '#525252')));

              // Midpoint for label
              const midX = (fromPt.x + toPt.x) / 2;
              const midY = (fromPt.y + toPt.y) / 2;

              // Guard: skip rendering this edge if any coordinate is not finite
              const allCoordsValid = [fromPt.x, fromPt.y, toPt.x, toPt.y, midX, midY].every(Number.isFinite);

              if (!allCoordsValid) return null;

              return (
                <g key={`edge-${edge.id}`}>
                  <motion.line
                    x1={fromPt.x} y1={fromPt.y}
                    x2={toPt.x} y2={toPt.y}
                    stroke={edgeColor}
                    strokeWidth={isHighlighted ? "2.5" : "1.8"}
                    strokeDasharray={edge.dashed ? "6 4" : "none"}
                    markerEnd={`url(#arrow-${edge.id}-${step})`}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, delay: edgeIndex * 0.1 }}
                  />
                  {/* Animated data flow particle */}
                  {!edge.dashed && fromNode.color !== 'transparent' && (
                    <motion.circle
                      r="4"
                      fill={fromNode.color || '#3b82f6'}
                      cx={fromPt.x}
                      cy={fromPt.y}
                      animate={{
                        cx: [fromPt.x, toPt.x],
                        cy: [fromPt.y, toPt.y],
                        opacity: [0, 1, 1, 0],
                        scale: [0.5, 1.2, 1.2, 0.5]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: edgeIndex * 0.4
                      }}
                      style={{ filter: `drop-shadow(0 0 6px ${fromNode.color || '#3b82f6'})` }}
                    />
                  )}
                  {/* Dashed edge: render X cross particle instead */}
                  {edge.dashed && (
                    <motion.g
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 + edgeIndex * 0.1, type: 'spring' }}
                    >
                      <circle cx={midX} cy={midY} r="8" fill={isLight ? '#fef2f2' : '#1c1013'} stroke="#ef4444" strokeWidth="1.5" />
                      <line x1={midX - 3.5} y1={midY - 3.5} x2={midX + 3.5} y2={midY + 3.5} stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
                      <line x1={midX + 3.5} y1={midY - 3.5} x2={midX - 3.5} y2={midY + 3.5} stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
                    </motion.g>
                  )}
                  {/* ─── EDGE LABEL ─── */}
                  {edge.label && (
                    <motion.g
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 + edgeIndex * 0.1 }}
                    >
                      <rect
                        x={midX - (edge.label.length * 3.5 + 10)}
                        y={midY - 10}
                        width={edge.label.length * 7 + 20}
                        height={18}
                        rx="9"
                        fill={edge.dashed ? (isLight ? '#fef2f2' : '#1c1013') : (isLight ? '#f8fafc' : '#18181b')}
                        stroke={edge.dashed ? '#fca5a5' : (isLight ? '#e2e8f0' : '#27272a')}
                        strokeWidth="1"
                        opacity="0.95"
                      />
                      <text
                        x={midX}
                        y={midY + 3}
                        fill={edge.dashed ? '#ef4444' : (isLight ? '#64748b' : '#a1a1aa')}
                        fontSize="9"
                        textAnchor="middle"
                        fontWeight="700"
                        style={{ letterSpacing: '0.3px', textTransform: 'uppercase' }}
                      >
                        {edge.label}
                      </text>
                    </motion.g>
                  )}
                </g>
              );
            })}
          </AnimatePresence>

          {/* ─── 2. DRAW NODES (Enhanced with gradient fills + glow) ─── */}
          <AnimatePresence>
            {[...(canvas.nodes || [])]
              .filter(n => n.type !== 'text')
              .map((node, nodeIndex) => {
                const { width, height, lines, type, isBox } = getNodeDims(node);
                const isContainer = isBox && (node.isContainer === true || height >= 100);

                const scaledX = safeCoord(node.x) * SCALE_X;
                const scaledY = safeCoord(node.y) * SCALE_Y;

                // Color-based semantic styling
                const nodeColor = node.color || '#3b82f6';
                const isSuccess = nodeColor === '#10b981' || nodeColor === '#22c55e';
                const isDanger = nodeColor === '#ef4444' || nodeColor === '#f87171';
                const isWarning = nodeColor === '#f59e0b' || nodeColor === '#eab308';
                const isNeutral = nodeColor === '#9ca3af' || nodeColor === '#64748b' || nodeColor === '#6b7280';

                // Determine border strength based on semantic meaning
                const borderColor = node.stroke || nodeColor;
                const borderWidth = isDanger ? 2 : 1.5;

                return (
                  <motion.g
                    key={`bg-${node.id}`}
                    layoutId={`node-bg-${node.id}`}
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.7, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25, delay: nodeIndex * 0.06 }}
                    filter={node.color && type !== 'divider' ? `url(#glow-${node.id}-${step})` : undefined}
                  >
                    <foreignObject
                      x={scaledX - width / 2 - 10} y={scaledY - height / 2 - 10}
                      width={width + 20} height={height + 20}
                      style={{ overflow: 'visible' }}
                    >
                      <div
                        title={node.label}
                        style={{
                          position: 'absolute',
                          top: 10, left: 10,
                          width: width, height: height,
                          borderRadius: type === 'box' ? '16px' : (type === 'divider' ? '0px' : `${height / 2}px`),
                          background: type === 'divider' ? 'transparent' :
                            (isLight
                              ? `linear-gradient(135deg, ${nodeColor}08 0%, ${nodeColor}18 100%)`
                              : `linear-gradient(135deg, ${nodeColor}15 0%, ${nodeColor}30 100%)`
                            ),
                          backdropFilter: type === 'divider' ? 'none' : 'blur(16px)',
                          WebkitBackdropFilter: type === 'divider' ? 'none' : 'blur(16px)',
                          border: type === 'divider' ? 'none' : `${borderWidth}px ${node.dashed ? 'dashed' : 'solid'} ${borderColor}${isLight ? '50' : '60'}`,
                          borderLeft: type === 'divider' && height >= width ? `2px ${node.dashed ? 'dashed' : 'solid'} ${node.stroke || '#818cf8'}` : undefined,
                          borderTop: type === 'divider' && width > height ? `2px ${node.dashed ? 'dashed' : 'solid'} ${node.stroke || '#818cf8'}` : undefined,
                          boxShadow: type === 'divider' ? 'none' :
                            `0 4px 20px ${nodeColor}${isLight ? '12' : '20'}, inset 0 1px 0 ${nodeColor}${isLight ? '10' : '15'}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'visible',
                          padding: '0 12px',
                          boxSizing: 'border-box',
                          position: 'relative'
                        }}
                      >
                        {/* Gradient color bar on left */}
                        {type !== 'divider' && (
                          <div style={{
                            position: 'absolute', top: 0, bottom: 0, left: 0, width: '4px',
                            background: `linear-gradient(180deg, ${borderColor} 0%, ${borderColor}80 100%)`,
                            borderRadius: type === 'box' ? '16px 0 0 16px' : `${height / 2}px 0 0 ${height / 2}px`,
                            opacity: 0.9
                          }} />
                        )}

                        {/* Status icon indicator */}
                        {type !== 'divider' && (isSuccess || isDanger || isNeutral) && (
                          <div style={{
                            position: 'absolute', top: -6, right: -6,
                            width: 18, height: 18,
                            borderRadius: '50%',
                            backgroundColor: isSuccess ? '#10b981' : (isDanger ? '#ef4444' : '#6b7280'),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '10px', color: '#fff', fontWeight: 900,
                            boxShadow: `0 2px 8px ${isSuccess ? '#10b981' : (isDanger ? '#ef4444' : '#6b7280')}40`,
                            border: `2px solid ${isLight ? '#fff' : '#0a0a0c'}`,
                            zIndex: 10
                          }}>
                            {isSuccess ? '✓' : (isDanger ? '✕' : '●')}
                          </div>
                        )}

                        <div style={{
                          position: 'relative',
                          zIndex: 1,
                          color: resolveTextColor(node.textColor, isContainer),
                          fontSize: node.fontSize ? `${Number(node.fontSize)}px` : "11.5px",
                          textAlign: "center",
                          fontWeight: "800",
                          letterSpacing: '0.4px',
                          textTransform: 'uppercase',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          lineHeight: 1.3,
                          overflow: 'visible',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          opacity: 1
                        }}>
                          {!isContainer && node.label}
                        </div>
                      </div>
                    </foreignObject>

                    {/* EXTERNAL CONTAINER LABEL (Pinned Above) */}
                    {isContainer && node.label && (
                      <text
                        x={scaledX} y={scaledY - height / 2 - 18}
                        fill={resolveTextColor(node.textColor, true)}
                        fontSize="11"
                        textAnchor="middle"
                        fontWeight="900"
                        style={{ letterSpacing: '1.5px', textTransform: 'uppercase', opacity: 0.8 }}
                      >
                        {node.label}
                      </text>
                    )}


                  </motion.g>
                );
              })}
          </AnimatePresence>

          {/* ─── 3. DRAW TEXT NODES ─── */}
          <AnimatePresence>
            {[...(canvas.nodes || [])]
              .filter(n => n.type === 'text')
              .map((node) => {
                return (
                  <motion.g key={`txt-${node.id}`} layoutId={`node-txt-${node.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <text
                      x={safeCoord(node.x) * SCALE_X} y={safeCoord(node.y) * SCALE_Y}
                      fill={resolveTextColor(node.textColor)}
                      fontSize={node.fontSize || "14"}
                      textAnchor={node.align || "start"}
                      fontWeight="600"
                    >
                      {node.label}
                    </text>
                  </motion.g>
                );
              })}
          </AnimatePresence>
        </svg>
      </div>
    );
  };

  return (
    <div
      style={{
        width: '100%', margin: '0 auto', background: 'var(--bg-paper)',
        borderRadius: '28px', border: '1px solid var(--border-main)',
        overflow: 'hidden', color: 'var(--text-primary)',
        boxShadow: 'var(--shadow-lg)'
      }}
    >
      <style>{`
        @media (max-width: 600px) {
          .concept-visualizer-panels { flex-direction: column !important; }
          .concept-left-panel { width: 100% !important; min-width: 0 !important; border-right: none !important; border-bottom: 1px solid var(--border-main) !important; max-height: 200px; overflow-y: auto; }
          .cv-mobile-hint { display: flex !important; }
        }
      `}</style>

      {/* ── MOBILE HINT BANNER ── */}
      <div className="cv-mobile-hint" style={{
        display: 'none', alignItems: 'center',
        gap: '10px', padding: '8px 14px',
        background: isLight ? 'rgba(59,130,246,0.07)' : 'rgba(59,130,246,0.1)',
        borderBottom: '1px solid rgba(59,130,246,0.2)',
        fontSize: '12px', color: isLight ? '#1d4ed8' : '#93c5fd',
        fontWeight: 600, boxSizing: 'border-box', flexShrink: 0
      }}>
        <span>💻 Switch to a laptop for the full interactive experience.</span>
      </div>

      {/* HEADER */}
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', background: 'var(--bg-glass)',
          backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--border-main)'
        }}
      >
        <div>
          <div style={{ color: '#3b82f6', fontWeight: 900, letterSpacing: '2px', textTransform: 'uppercase', lineHeight: 1, fontSize: '11px' }}>Visual Concept</div>
          <div style={{ fontWeight: 900, letterSpacing: '-0.3px', color: 'var(--text-primary)', lineHeight: 1.2, fontSize: '22px', marginTop: '4px' }}>{title}</div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '6px 16px',
              backgroundColor: isLight ? '#f1f5f9' : '#18181b',
              color: step === 0 ? (isLight ? '#94a3b8' : '#3f3f46') : (isLight ? '#475569' : '#e2e8f0'),
              border: isLight ? '1px solid #cbd5e1' : '1px solid #27272a',
              borderRadius: '12px',
              fontWeight: 700, fontSize: '13px',
              cursor: step === 0 ? 'default' : 'pointer'
            }}
          >
            <ChevronLeft size={16} /> Previous
          </button>
          <button
            onClick={() => setStep(Math.min(frames.length - 1, step + 1))}
            disabled={isComplete}
            style={{
              padding: '6px 20px',
              backgroundColor: isComplete ? 'rgba(6, 95, 70, 0.2)' : '#2563eb',
              color: isComplete ? '#34d399' : '#fff',
              border: isComplete ? '1px solid #065f46' : 'none',
              borderRadius: '12px',
              fontWeight: 800, fontSize: '13px',
              cursor: isComplete ? 'default' : 'pointer',
              boxShadow: isComplete ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.3)'
            }}
          >
            {isComplete ? 'Completed ✓' : 'Next Step'}
          </button>
        </div>
      </div>

      {/* ─── ROW 2: PATTERN OVERVIEW (Full Width) ─── */}
      {metadata.patternOverview && (
        <div style={{ padding: '16px 20px', background: isLight ? '#ffffff' : '#0f0f12', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22', boxSizing: 'border-box' }}>
          <div style={{
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid var(--border-main)',
            position: 'relative',
            overflow: 'hidden',
            background: isLight ? 'rgba(59, 130, 246, 0.03)' : 'rgba(59, 130, 246, 0.05)',
            boxShadow: `0 4px 12px ${isLight ? 'rgba(59, 130, 246, 0.05)' : 'rgba(0, 0, 0, 0.2)'}`
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, bgcolor: '#3b82f6', opacity: 0.6 }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.1rem' }}>🎯</span>
                <span style={{ fontSize: '9px', fontWeight: 700, color: isLight ? '#475569' : '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.8 }}>Pattern Overview</span>
              </div>
            </div>
            <div className="markdown-overview" style={{
              fontSize: '15px',
              color: isLight ? '#334155' : '#cbd5e1',
              lineHeight: 1.6,
              fontWeight: 400
            }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  p: ({ node, ...props }) => <p style={{ margin: '0 0 12px 0' }} {...props} />,
                  ul: ({ node, ...props }) => <ul style={{ margin: '0 0 12px 0', paddingLeft: '18px' }} {...props} />,
                  li: ({ node, ...props }) => <li style={{ marginBottom: '4px' }} {...props} />,
                  strong: ({ node, ...props }) => <strong style={{ color: isLight ? '#0f172a' : '#f8fafc', fontWeight: 800 }} {...props} />,
                  code: ({ node, inline, ...props }) => (
                    <code style={{
                      backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      fontSize: '0.9em',
                      fontFamily: 'JetBrains Mono, monospace'
                    }} {...props} />
                  )
                }}
              >
                {metadata.patternOverview}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* ─── ROW 3: PROBLEM + USE CASES (Side-by-Side Context) ─── */}
      {!!(((metadata.description || metadata.problem_statement) && categoryId === 'dsa') || (metadata.common_use_cases && metadata.common_use_cases.length > 0)) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 20px', background: isLight ? '#f8fafc' : '#0a0a0c', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22', boxSizing: 'border-box' }}>
          {/* Problem Statement Card (DSA Only) */}
          {categoryId === 'dsa' && (metadata.description || metadata.problem_statement) && (
            <div style={{
              flex: 1,
              padding: '16px',
              borderRadius: '12px',
              background: isLight ? '#f9fafb' : '#0d0d10',
              border: `1px solid ${isLight ? '#e5e7eb' : '#1f2937'}`,
              boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px', color: '#6366f1' }}>📄</span>
                <span style={{ fontSize: '9px', fontWeight: 700, color: isLight ? '#475569' : '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.8 }}>Problem Statement</span>
              </div>
              <div style={{
                margin: 0,
                fontSize: '15px',
                color: isLight ? '#1f2937' : '#e5e7eb',
                lineHeight: 1.5,
                fontWeight: 500,
                whiteSpace: 'pre-line'
              }}>
                {metadata.description || metadata.problem_statement}
              </div>
            </div>
          )}

          {/* Use Cases Card */}
          {metadata.common_use_cases && metadata.common_use_cases.length > 0 && (
            <div style={{ flex: '0 1 280px', minWidth: '220px', padding: '12px 16px', borderRadius: '8px', background: isLight ? '#fffbeb' : '#1a1520', border: `1px solid ${isLight ? '#fcd34d40' : '#7c3aed30'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px' }}>💡</span>
                <span style={{ fontSize: '9px', fontWeight: 700, color: isLight ? '#475569' : '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.8 }}>Common Use Cases</span>
              </div>
              <ul style={{ margin: 0, paddingLeft: '14px', fontSize: '13px', color: isLight ? '#78350f' : '#fde68a', lineHeight: 1.7 }}>
                {metadata.common_use_cases.slice(0, 5).map((c, i) => (
                  <li key={i}>{renderInlineContent(c, '#f59e0b', isLight)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ─── ROW 4: INTERACTIVE VISUALIZER WORKSPACE ─── */}
      <div ref={visualizerRef} style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', maxHeight: '1000px', background: isLight ? '#f8fafc' : '#0f0f12', overflow: 'hidden' }}>
        <div className="concept-visualizer-panels" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* LEFT PANEL (Sidebar) */}
          <div className="concept-left-panel" style={{ width: '25%', minWidth: '200px', padding: '16px', background: isLight ? '#ffffff' : '#18181b', borderRight: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>

            {/* Example card */}
            {metadata.example && (
              <div style={{ padding: '12px 16px', borderRadius: '8px', background: isLight ? '#fff' : '#141417', border: `1px solid ${isLight ? '#e2e8f0' : '#1f1f22'}`, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px' }}>📝</span>
                  <span style={{ fontSize: '9px', fontWeight: 700, color: isLight ? '#475569' : '#94a3b8', letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.8 }}>EXAMPLE:</span>
                </div>
                {metadata.example.input && <div style={{ fontSize: '14px', color: isLight ? '#1e293b' : '#f8fafc' }}><strong>Input:</strong> <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', color: isLight ? '#1d4ed8' : '#60a5fa' }}>{metadata.example.input}</span></div>}
                {metadata.example.output && <div style={{ fontSize: '14px', color: isLight ? '#1e293b' : '#f8fafc' }}><strong>Output:</strong> <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', color: '#22c55e' }}>{metadata.example.output}</span></div>}
                {metadata.example.explanation && (
                  <div className="markdown-content" style={{ fontSize: '12px', color: isLight ? '#64748b' : '#94a3b8', marginTop: '4px', lineHeight: 1.4 }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        p: ({ node, ...props }) => <span {...props} />, // Prevent extra margins
                        ul: ({ node, ...props }) => <ul style={{ margin: '4px 0', paddingLeft: '16px' }} {...props} />,
                        li: ({ node, ...props }) => <li style={{ marginBottom: '2px' }} {...props} />
                      }}
                    >
                      {metadata.example.explanation}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            )}

            <div>
              <span
                style={{
                  display: 'inline-block',
                  backgroundColor: '#3b82f615',
                  color: '#60a5fa',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '10px',
                  border: '1px solid #3b82f630',
                  padding: '4px 12px'
                }}
              >
                {currentFrame.concept}
              </span>
              <p style={{ fontSize: '1rem', lineHeight: '1.6', color: isLight ? '#334155' : '#e2e8f0', fontWeight: 500, margin: 0 }}>{currentFrame.layman}</p>
            </div>

            <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: isLight ? '1px solid #e2e8f0' : '1px solid #27272a' }}>
              <div
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: isLight ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.2) 100%)',
                  border: isLight ? '1px solid #bbf7d0' : '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: isLight ? '0 4px 12px rgba(16, 185, 129, 0.1)' : '0 4px 12px rgba(16, 185, 129, 0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                  boxSizing: 'border-box'
                }}
              >
                <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                  <Lightbulb size={14} />
                </div>
                <div>
                  <div style={{ fontSize: '9px', color: isLight ? '#065f46' : '#34d399', fontWeight: 900, textTransform: 'uppercase', lineHeight: 1 }}>Key Takeaway</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: isLight ? '#0f172a' : '#f8fafc', fontFamily: 'JetBrains Mono, monospace', marginTop: '2px' }}>{currentFrame.formula || "Observe the pattern"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL (Main Visual Content) */}
          <div style={{ flex: 3, display: 'flex', flexDirection: 'column', background: isLight ? 'radial-gradient(circle at center, #ffffff 0%, #f1f5f9 100%)' : 'radial-gradient(circle at center, #111114 0%, #0a0a0c 100%)' }}>
            <div style={{ height: `${canvasHeight}px`, minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px', position: 'relative' }}>
              {renderCanvas()}
              <span style={{ position: 'absolute', top: 16, right: 24, fontSize: '11px', color: isLight ? '#94a3b8' : '#3f3f46', fontWeight: 900, textTransform: 'uppercase' }}>Phase {step + 1} / {frames.length}</span>
            </div>
            <div
              onMouseDown={handleDragStart}
              style={{ height: '4px', backgroundColor: isLight ? '#e2e8f0' : '#1f1f22', cursor: 'row-resize' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3b82f6')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isLight ? '#e2e8f0' : '#1f1f22')}
            />
            <AnimatePresence mode="wait">
              {currentFrame.code ? (
                <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} style={{ flex: 1, padding: '20px', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: 6, height: 6, backgroundColor: '#3b82f6', borderRadius: '50%' }} />
                      <span style={{ fontSize: '9px', fontWeight: 900, textTransform: 'uppercase' }}>Implementation Snippet</span>
                    </div>
                    <span style={{ fontSize: '9px', fontWeight: 900, textTransform: 'uppercase', backgroundColor: '#10b98110', border: '1px solid #10b98120', color: '#10b981', padding: '4px 10px', borderRadius: '12px' }}>
                      {currentFrame.language || 'javascript'}
                    </span>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', backgroundColor: isLight ? '#f8fafc' : '#0a0a0c', borderRadius: '12px', padding: '16px', border: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22' }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'JetBrains Mono, monospace', color: isLight ? '#334155' : '#94a3b8' }}><code>{currentFrame.code}</code></pre>
                  </div>
                </motion.div>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3f3f46', fontSize: '11px' }}>No implementation snippet.</div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConceptVisualizer;